import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { API_URL, secret_key, storedData } from '../Constants';
import { Card, CardHeader, CardContent, Button } from '../ui';
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';

export default function ExecutiveDashboard() {
  // Decrypt token and user info
  const encryptedToken = localStorage.getItem('token');
  let token = '';
  if (encryptedToken) {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, secret_key);
    token = `Bearer ${bytes.toString(CryptoJS.enc.Utf8)}`;
  }
  let loggedUser = null;
  if (storedData) {
    const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
    loggedUser = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  // State
  const [metrics, setMetrics] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [smsStats, setSmsStats] = useState({});
  const [vendorBalances, setVendorBalances] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  const COLORS = ['#4A90E2', '#50E3C2', '#F5A623', '#D0021B', '#9013FE'];

  // Theme toggle
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');

    const headers = { Authorization: token };
    const cid = loggedUser?.company_id;

    const fetchData = async () => {
      try {
        const companyRes = await axios.get(`${API_URL}/get_company/${cid}`, { headers });

        const creditRes = await axios.get(`${API_URL}/get_credit_history/${cid}`, { headers });
        setCreditHistory(creditRes.data.map(c => ({ date: c.created_at, credits: c.units })));

        const smsRes = await axios.get(`${API_URL}/get_sms_stats/${cid}`, { headers });
        setSmsStats(smsRes.data);

        if (loggedUser?.role_id === 4) {
          const [onfonRes, mtechRes] = await Promise.all([
            axios.get(`${API_URL}/get_onfon_bal`, { headers }),

          ]);

          setVendorBalances([
            { name: 'Onfon', balance: onfonRes.data }
          ]);
        }
      } catch (err) {
        console.error('Error loading dashboard data', err);
      }
    };

    fetchData();
  }, [theme]);

  const smsDistribution = [
    { name: 'Today', value: smsStats.total_sent_today || 0 },
    { name: 'Yesterday', value: smsStats.total_sent_yesterday || 0 },
    { name: 'All Time', value: smsStats.sms_count || 0 }
  ];

  return (
    <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 dark:bg-gray-900 dark:text-white transition-colors duration-300">
      {/* Theme Toggle */}
      <div className="lg:col-span-3 flex justify-end mb-2">
        <button
          onClick={toggleTheme}
          className="px-4 py-2 rounded-full bg-gray-200 dark:bg-gray-800 dark:text-white text-sm hover:shadow-md transition"
        >
          Toggle {theme === 'dark' ? 'Light' : 'Dark'} Mode
        </button>
      </div>

      {/* KPI Cards */}
      <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {metrics.map((m, i) => (
          <Card key={i} className="text-center dark:bg-gray-800">
            <CardHeader>
              <h5 className="text-sm font-medium text-gray-600 dark:text-gray-300">{m.label}</h5>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{m.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Credit History */}
      <Card className="xl:col-span-2 dark:bg-gray-800">
        <CardHeader>
          <h4 className="text-lg font-semibold">Credit History</h4>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={creditHistory} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="credits" stroke={COLORS[0]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* SMS Trends */}
      <Card className="xl:col-span-1 dark:bg-gray-800">
        <CardHeader>
          <h4 className="text-lg font-semibold">SMS Trends</h4>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={smsDistribution} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill={COLORS[1]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Vendor Balances */}
      {vendorBalances.length > 0 && (
        <Card className="xl:col-span-2 dark:bg-gray-800">
          <CardHeader>
            <h4 className="text-lg font-semibold">Vendor Balances</h4>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={vendorBalances}
                  dataKey="balance"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {vendorBalances.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={value => value.toFixed(2)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* SMS Distribution */}
      <Card className="xl:col-span-1 dark:bg-gray-800">
        <CardHeader>
          <h4 className="text-lg font-semibold">SMS Distribution</h4>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={smsDistribution}
                dataKey="value"
                nameKey="name"
                innerRadius={40}
                outerRadius={80}
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {smsDistribution.map((entry, idx) => (
                  <Cell key={`slice-${idx}`} fill={COLORS[(idx + 2) % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
